// The channel vocabulary of the Content Intelligence features — routes, stores and the Cooper wire
// all speak this. The integrations `CHANNEL` enum names the same platforms differently (Meta Ads is
// `facebook` there); `channelConfig` converts at the few calls that reach integrations.
export const INTELLIGENCE_CHANNEL = {
  linkedinAds: 'linkedin_ads',
  metaAds: 'meta_ads',
  linkedinSocial: 'linkedin_social',
} as const;
export type IntelligenceChannel = (typeof INTELLIGENCE_CHANNEL)[keyof typeof INTELLIGENCE_CHANNEL];
export const INTELLIGENCE_CHANNELS: IntelligenceChannel[] = Object.values(INTELLIGENCE_CHANNEL);
// The individual members, for types that discriminate on one channel.
export type LinkedinAdsChannel = typeof INTELLIGENCE_CHANNEL.linkedinAds;
export type MetaAdsChannel = typeof INTELLIGENCE_CHANNEL.metaAds;

export const PLAYBOOK_GENERATION_SOURCE_KIND = {
  webpage: 'webpage',
  file: 'file',
  sampleText: 'sample_text',
  linkedinAds: 'linkedin_ads',
  metaAds: 'meta_ads',
  linkedinSocial: 'linkedin_social',
} as const;
export type PlaybookGenerationSourceKind =
  (typeof PLAYBOOK_GENERATION_SOURCE_KIND)[keyof typeof PLAYBOOK_GENERATION_SOURCE_KIND];

export const PLAYBOOK_GENERATION_RUN_STATUS = {
  collectingContext: 'collecting_context',
  contextLocked: 'context_locked',
  generatingAudiences: 'generating_audiences',
  audiencesReady: 'audiences_ready',
  generatingPlaybooks: 'generating_playbooks',
  playbooksReady: 'playbooks_ready',
  approved: 'approved',
  failed: 'failed',
} as const;
export type PlaybookGenerationRunStatus =
  (typeof PLAYBOOK_GENERATION_RUN_STATUS)[keyof typeof PLAYBOOK_GENERATION_RUN_STATUS];

// Shared by TargetAudienceOriginEnum and PlaybookOrigin (same values on the backend).
export const ORIGIN = {
  generated: 'generated',
  manual: 'manual',
} as const;
export type Origin = (typeof ORIGIN)[keyof typeof ORIGIN];

// Every objective the channels offer; `channelConfig` narrows it to the ones a channel accepts.
// Backend enum values; the display strings live in the label map below.
export const PLAYBOOK_OBJECTIVE = {
  brandAwareness: 'brand_awareness',
  websiteVisits: 'website_visits',
  engagement: 'engagement',
  videoViews: 'video_views',
  leadGeneration: 'lead_generation',
  websiteConversions: 'website_conversions',
  jobApplicants: 'job_applicants',
  talentLeads: 'talent_leads',
  awareness: 'awareness',
  traffic: 'traffic',
  leads: 'leads',
  appPromotion: 'app_promotion',
  sales: 'sales',
} as const;
export type PlaybookObjective = (typeof PLAYBOOK_OBJECTIVE)[keyof typeof PLAYBOOK_OBJECTIVE];

export const PLAYBOOK_OBJECTIVE_LABEL: Record<PlaybookObjective, string> = {
  [PLAYBOOK_OBJECTIVE.brandAwareness]: 'Brand awareness',
  [PLAYBOOK_OBJECTIVE.websiteVisits]: 'Website visits',
  [PLAYBOOK_OBJECTIVE.engagement]: 'Engagement',
  [PLAYBOOK_OBJECTIVE.videoViews]: 'Video views',
  [PLAYBOOK_OBJECTIVE.leadGeneration]: 'Lead generation',
  [PLAYBOOK_OBJECTIVE.websiteConversions]: 'Website conversions',
  [PLAYBOOK_OBJECTIVE.jobApplicants]: 'Job applicants',
  [PLAYBOOK_OBJECTIVE.talentLeads]: 'Talent leads',
  [PLAYBOOK_OBJECTIVE.awareness]: 'Awareness',
  [PLAYBOOK_OBJECTIVE.traffic]: 'Traffic',
  [PLAYBOOK_OBJECTIVE.leads]: 'Leads',
  [PLAYBOOK_OBJECTIVE.appPromotion]: 'App promotion',
  [PLAYBOOK_OBJECTIVE.sales]: 'Sales',
};

export const LINKEDIN_OBJECTIVES: PlaybookObjective[] = [
  PLAYBOOK_OBJECTIVE.brandAwareness,
  PLAYBOOK_OBJECTIVE.websiteVisits,
  PLAYBOOK_OBJECTIVE.engagement,
  PLAYBOOK_OBJECTIVE.videoViews,
  PLAYBOOK_OBJECTIVE.leadGeneration,
  PLAYBOOK_OBJECTIVE.websiteConversions,
  PLAYBOOK_OBJECTIVE.jobApplicants,
  PLAYBOOK_OBJECTIVE.talentLeads,
];

// An organic post carries no campaign objective. Engagement is the only one it can mean.
export const LINKEDIN_SOCIAL_OBJECTIVES: PlaybookObjective[] = [PLAYBOOK_OBJECTIVE.engagement];

// The six ODAX outcomes.
export const META_OBJECTIVES: PlaybookObjective[] = [
  PLAYBOOK_OBJECTIVE.awareness,
  PLAYBOOK_OBJECTIVE.traffic,
  PLAYBOOK_OBJECTIVE.engagement,
  PLAYBOOK_OBJECTIVE.leads,
  PLAYBOOK_OBJECTIVE.appPromotion,
  PLAYBOOK_OBJECTIVE.sales,
];

// The targeting vocabularies. A channel that targets an audience picks one. The two share no facet,
// so the column set and the form body follow the vocabulary, not the channel.
export const AUDIENCE_FACET_KIND = {
  linkedin: 'linkedin',
  meta: 'meta',
} as const;
export type AudienceFacetKind = (typeof AUDIENCE_FACET_KIND)[keyof typeof AUDIENCE_FACET_KIND];

// Facet value sets — must match the backend's allowed LinkedInFacets strings.
export const LINKEDIN_SENIORITY = {
  unpaid: 'Unpaid',
  training: 'Training',
  entry: 'Entry',
  senior: 'Senior',
  manager: 'Manager',
  director: 'Director',
  vp: 'VP',
  cxo: 'CXO',
  partner: 'Partner',
  owner: 'Owner',
} as const;
export type LinkedinSeniority = (typeof LINKEDIN_SENIORITY)[keyof typeof LINKEDIN_SENIORITY];
export const LINKEDIN_SENIORITIES: LinkedinSeniority[] = Object.values(LINKEDIN_SENIORITY);

export const LINKEDIN_COMPANY_SIZE = {
  size1: '1',
  size2to10: '2-10',
  size11to50: '11-50',
  size51to200: '51-200',
  size201to500: '201-500',
  size501to1000: '501-1000',
  size1001to5000: '1001-5000',
  size5001to10000: '5001-10000',
  size10001plus: '10001+',
} as const;
export type LinkedinCompanySize =
  (typeof LINKEDIN_COMPANY_SIZE)[keyof typeof LINKEDIN_COMPANY_SIZE];
export const LINKEDIN_COMPANY_SIZES: LinkedinCompanySize[] = Object.values(LINKEDIN_COMPANY_SIZE);

// The only two values the backend accepts; targeting everyone is an empty list rather than a third
// value, so the form's three-way choice maps through META_GENDER_SELECTION_FACET below.
export const META_GENDER = {
  male: 'male',
  female: 'female',
} as const;
export type MetaGender = (typeof META_GENDER)[keyof typeof META_GENDER];

export const META_GENDER_SELECTION = {
  all: 'all',
  men: 'men',
  women: 'women',
} as const;
export type MetaGenderSelection =
  (typeof META_GENDER_SELECTION)[keyof typeof META_GENDER_SELECTION];
export const META_GENDER_SELECTIONS: MetaGenderSelection[] = Object.values(META_GENDER_SELECTION);

export const META_GENDER_SELECTION_LABEL: Record<MetaGenderSelection, string> = {
  [META_GENDER_SELECTION.all]: 'All',
  [META_GENDER_SELECTION.men]: 'Men',
  [META_GENDER_SELECTION.women]: 'Women',
};

export const META_GENDER_SELECTION_FACET: Record<MetaGenderSelection, MetaGender[]> = {
  [META_GENDER_SELECTION.all]: [],
  [META_GENDER_SELECTION.men]: [META_GENDER.male],
  [META_GENDER_SELECTION.women]: [META_GENDER.female],
};

// The backend rejects ages outside these bounds and requires both when a range is set.
export const META_AGE_MIN = 18;
export const META_AGE_MAX = 65;

// The wizard is an FE stepper; the run status is the source of truth and maps onto it.
export const PLAYBOOKS_WIZARD_STEP = {
  context: 'context',
  audiences: 'audiences',
  playbooks: 'playbooks',
} as const;
export type PlaybooksWizardStep =
  (typeof PLAYBOOKS_WIZARD_STEP)[keyof typeof PLAYBOOKS_WIZARD_STEP];

export const PLAYBOOKS_WIZARD_STEPS: PlaybooksWizardStep[] = [
  PLAYBOOKS_WIZARD_STEP.context,
  PLAYBOOKS_WIZARD_STEP.audiences,
  PLAYBOOKS_WIZARD_STEP.playbooks,
];

export const MAX_WEBPAGE_GENERATION_SOURCES = 5;

// An insight's lifecycle. A run produces `pending` insights for review; saving the chosen ones
// moves them to `approved`. `rejected` exists on the backend but is never returned to the client.
export const PLAYBOOK_INSIGHT_STATUS = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;
export type PlaybookInsightStatus =
  (typeof PLAYBOOK_INSIGHT_STATUS)[keyof typeof PLAYBOOK_INSIGHT_STATUS];

export const PLAYBOOK_INSIGHT_RUN_STATUS = {
  generatingCopy: 'generating_copy',
  copyReady: 'copy_ready',
  analyzing: 'analyzing',
  insightsReady: 'insights_ready',
  completed: 'completed',
  failed: 'failed',
  rejected: 'rejected',
} as const;
export type PlaybookInsightRunStatus =
  (typeof PLAYBOOK_INSIGHT_RUN_STATUS)[keyof typeof PLAYBOOK_INSIGHT_RUN_STATUS];

// How one data source presents on a playbook card, derived from that source's insight run status:
// idle (run), processing (analysis running), ready (waiting at a gate), connected (insights saved).
export const PLAYBOOK_SOURCE_STATE = {
  idle: 'idle',
  processing: 'processing',
  ready: 'ready',
  connected: 'connected',
  failed: 'failed',
} as const;
export type PlaybookSourceState =
  (typeof PLAYBOOK_SOURCE_STATE)[keyof typeof PLAYBOOK_SOURCE_STATE];

// Why a source cannot be run right now. The backend refuses both cases, so the card disables the
// action and explains itself rather than letting the mutation come back with an error.
export const PLAYBOOK_SOURCE_BLOCKED_REASON = {
  runInProgress: 'run_in_progress',
  anywordRequired: 'anyword_required',
} as const;
export type PlaybookSourceBlockedReason =
  (typeof PLAYBOOK_SOURCE_BLOCKED_REASON)[keyof typeof PLAYBOOK_SOURCE_BLOCKED_REASON];

// The copy fields an ad can carry. The channel selects which ones a playbook uses. An ad pairs a
// headline with a long-form field. An organic post is the post alone.
export const PLAYBOOK_ASSET = {
  headline: 'headline',
  introductoryText: 'introductory_text',
  primaryText: 'primary_text',
  post: 'post',
} as const;
export type PlaybookAsset = (typeof PLAYBOOK_ASSET)[keyof typeof PLAYBOOK_ASSET];

export const PLAYBOOK_ASSET_LABEL: Record<PlaybookAsset, string> = {
  [PLAYBOOK_ASSET.headline]: 'Headline',
  [PLAYBOOK_ASSET.introductoryText]: 'Introductory text',
  [PLAYBOOK_ASSET.primaryText]: 'Primary text',
  [PLAYBOOK_ASSET.post]: 'Post',
};

export const PLAYBOOK_AD_VARIANT = {
  a: 'a',
  b: 'b',
} as const;
export type PlaybookAdVariant = (typeof PLAYBOOK_AD_VARIANT)[keyof typeof PLAYBOOK_AD_VARIANT];
export const PLAYBOOK_AD_VARIANTS: PlaybookAdVariant[] = Object.values(PLAYBOOK_AD_VARIANT);

export const PLAYBOOK_INSIGHT_SOURCE = {
  anyword: 'anyword_data',
  integration: 'integration_data',
  manualData: 'manual_data',
} as const;
export type PlaybookInsightSource =
  (typeof PLAYBOOK_INSIGHT_SOURCE)[keyof typeof PLAYBOOK_INSIGHT_SOURCE];

// What an insight claims. A directional one measured an effect and says which way to move; a pattern
// only describes how the account already writes, so it carries a copy count instead of a lift.
export const PLAYBOOK_INSIGHT_KIND = {
  directional: 'directional',
  pattern: 'pattern',
} as const;
export type PlaybookInsightKind =
  (typeof PLAYBOOK_INSIGHT_KIND)[keyof typeof PLAYBOOK_INSIGHT_KIND];

export const PLAYBOOK_INSIGHT_DIRECTION = {
  do: 'do',
  avoid: 'avoid',
} as const;
export type PlaybookInsightDirection =
  (typeof PLAYBOOK_INSIGHT_DIRECTION)[keyof typeof PLAYBOOK_INSIGHT_DIRECTION];

export const PLAYBOOK_INSIGHT_STEP = {
  copyReview: 'copy_review',
  insightsReview: 'insights_review',
} as const;
export type PlaybookInsightStep =
  (typeof PLAYBOOK_INSIGHT_STEP)[keyof typeof PLAYBOOK_INSIGHT_STEP];

// Every run goes straight to the insights: the Anyword source analyzes the copy it generates
// without pausing on it.
export const PLAYBOOK_INSIGHT_STEPS_BY_SOURCE: Record<
  PlaybookInsightSource,
  PlaybookInsightStep[]
> = {
  [PLAYBOOK_INSIGHT_SOURCE.anyword]: [PLAYBOOK_INSIGHT_STEP.insightsReview],
  [PLAYBOOK_INSIGHT_SOURCE.integration]: [PLAYBOOK_INSIGHT_STEP.insightsReview],
  [PLAYBOOK_INSIGHT_SOURCE.manualData]: [PLAYBOOK_INSIGHT_STEP.insightsReview],
};

// A run that reached copy_ready before the copy review was removed still waits on it.
export const LEGACY_COPY_REVIEW_STEPS: PlaybookInsightStep[] = [
  PLAYBOOK_INSIGHT_STEP.copyReview,
  PLAYBOOK_INSIGHT_STEP.insightsReview,
];

export const PLAYBOOK_TEMPLATE_RUN_STATUS = {
  generatingIdeas: 'generating_ideas',
  completed: 'completed',
  failed: 'failed',
  rejected: 'rejected',
} as const;
export type PlaybookTemplateRunStatus =
  (typeof PLAYBOOK_TEMPLATE_RUN_STATUS)[keyof typeof PLAYBOOK_TEMPLATE_RUN_STATUS];

// Why a completed playbook-wide run offers no template candidates.
export const PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON = {
  allPairsBuilt: 'all_pairs_built',
  allPairsContradict: 'all_pairs_contradict',
} as const;
export type PlaybookTemplateRunNoIdeasReason =
  (typeof PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON)[keyof typeof PLAYBOOK_TEMPLATE_RUN_NO_IDEAS_REASON];

// What an insight's template button does.
export const INSIGHT_TEMPLATE_STATE = {
  build: 'build',
  building: 'building',
  review: 'review',
  use: 'use',
} as const;
export type InsightTemplateState =
  (typeof INSIGHT_TEMPLATE_STATE)[keyof typeof INSIGHT_TEMPLATE_STATE];
